import { useCallback } from 'react';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { date as formatDate } from '@frameio/i18n';
import { useAccountPermissions } from '@frameio/permissions';
import {
  SubscriptionUpdateSource,
  UsageLimitExceeded,
} from '@frameio/plan-management/feature-gate/config';
import { usePlanManagementModalContext } from '@frameio/plan-management/modal/hooks';
import { getLink } from '@frameio/shared/routing/getLink';
import { AlertBanner } from '@frameio/vapor';
import { useIsOnUsagePage } from '../hooks/useIsOnUsagePage';
import type { AlertBannerProps } from '../types';
import { UpgradeBannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/GuestLimitAlertBanner';
import { StyledButton } from './StyledButton';
import { SupportButton } from './SupportButton';
import { UniversalLink } from './UniversalLink';

const SETTINGS_USERS = getLink({ page: 'settings', section: 'users' });

TEXT.DEFINE(
  {
    /** Title for billing managers */
    titleWithPermission: (
      seatsUsed: number,
      seatLimit: number,
      userRolePluralized: string
    ) =>
      `Guest Limit Exceeded (${seatsUsed} of ${seatLimit} ${userRolePluralized})`,

    /** Title for non-billing users */
    titleNoPermission: 'Guest Limit Exceeded',

    /** Message for Free/Trial users with upgrade option and grace period */
    detailsFreeWithUpgradeWithGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction,
      graceDate: Date
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>{' '}
        or <UniversalLink href={SETTINGS_USERS}>manage users</UniversalLink>{' '}
        before {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for Free/Trial users with upgrade option, no grace period */
    detailsFreeWithUpgradeNoGrace: (
      upgradedPlanTitle: string,
      openUpgradeModal: VoidFunction
    ) => (
      <>
        <StyledButton onClick={openUpgradeModal} variant="link">
          Subscribe to {upgradedPlanTitle}
        </StyledButton>{' '}
        or <UniversalLink href={SETTINGS_USERS}>manage users</UniversalLink>.
      </>
    ),

    /** Message for Free/Trial users without upgrade option, with grace period */
    detailsFreeNoUpgradeWithGrace: (graceDate: Date) => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Manage users</UniversalLink> before{' '}
        {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for Free/Trial users without upgrade option, no grace period */
    detailsFreeNoUpgradeNoGrace: () => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Manage users</UniversalLink>.
      </>
    ),

    /** Message for Enterprise billing managers */
    detailsEnterpriseWithPermission: () => (
      <>
        <UniversalLink href={SETTINGS_USERS}>Manage users</UniversalLink> or{' '}
        <SupportButton>contact support</SupportButton> for assistance.
      </>
    ),

    /** Message for paid plan billing managers with grace period */
    detailsPaidWithGrace: (graceDate: Date) => (
      <>
        <SupportButton>Contact support</SupportButton> or{' '}
        <UniversalLink href={SETTINGS_USERS}>manage users</UniversalLink> before{' '}
        {formatDate(graceDate, 'medium')} or account will be locked.
      </>
    ),

    /** Message for paid plan billing managers, no grace period */
    detailsPaidNoGrace: () => (
      <>
        <SupportButton>Contact support</SupportButton> or{' '}
        <UniversalLink href={SETTINGS_USERS}>manage users</UniversalLink>.
      </>
    ),

    /** Message for Adobe-managed accounts without billing permissions */
    detailsNoPermissionAdobe: 'Contact your admin for assistance.',

    /** Message for non-billing users with grace period */
    detailsNoPermissionWithGrace: (ownerEmail: string, graceDate: Date) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance before {formatDate(graceDate, 'medium')} or account will
        be locked.
      </>
    ),

    /** Message for non-billing users without grace period */
    detailsNoPermissionNoGrace: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance.
      </>
    ),
  },
  { StyledButton, UniversalLink, SupportButton, SETTINGS_USERS }
);

const getDetails = (
  canManageBilling: boolean,
  graceDate: Date | null,
  isEnterprise: boolean,
  isFreeOrTrialActive: boolean,
  isManagedByAdobe: boolean,
  openUpgradeModal: VoidFunction,
  ownerEmail: string,
  upgradedPlanTitle: string | undefined
) => {
  const hasGracePeriod = !!graceDate;

  if (!canManageBilling) {
    if (isManagedByAdobe) {
      return TEXT.detailsNoPermissionAdobe;
    }
    if (isEnterprise || !hasGracePeriod) {
      return TEXT.detailsNoPermissionNoGrace(ownerEmail);
    }
    return TEXT.detailsNoPermissionWithGrace(ownerEmail, graceDate);
  }

  if (isFreeOrTrialActive) {
    if (upgradedPlanTitle) {
      if (hasGracePeriod) {
        return TEXT.detailsFreeWithUpgradeWithGrace(
          upgradedPlanTitle,
          openUpgradeModal,
          graceDate
        );
      }
      return TEXT.detailsFreeWithUpgradeNoGrace(
        upgradedPlanTitle,
        openUpgradeModal
      );
    }
    if (hasGracePeriod) {
      return TEXT.detailsFreeNoUpgradeWithGrace(graceDate);
    }
    return TEXT.detailsFreeNoUpgradeNoGrace();
  }

  if (isEnterprise) {
    return TEXT.detailsEnterpriseWithPermission();
  }

  if (hasGracePeriod) {
    return TEXT.detailsPaidWithGrace(graceDate);
  }

  return TEXT.detailsPaidNoGrace();
};

export function GuestLimitAlertBanner({
  account,
  fetchPlansData,
  upgradedPlan,
}: AlertBannerProps) {
  const accountId = account?.id;
  const canManageBilling =
    useAccountPermissions({
      accountId,
      check: 'canManageBilling',
    }) ?? false;

  const planManagementModalContext = usePlanManagementModalContext();

  const openUpgradeModal = useCallback(() => {
    if (!upgradedPlan) return;

    planManagementModalContext?.setSelectedPlanWithFeature({
      ...upgradedPlan,
      usageLimitExceeded: UsageLimitExceeded.GUEST,
      subscriptionUpdateSource: SubscriptionUpdateSource.OVER_LIMIT_BANNER,
    });
  }, [upgradedPlan, planManagementModalContext]);

  const title = upgradedPlan?.title;

  const guestSeatsUsed = account?.usageByLicenseType?.guest?.seatsUsed ?? 0;
  const guestSeatLimit = account?.usageByLicenseType?.guest?.seatLimit ?? 0;
  const ownerEmail = account?.owner?.email ?? '';

  const subscription = account?.subscription;
  const tier = subscription?.tier;
  const trial = subscription?.trial;
  const isFree = tier === 'FREE';
  const isEnterprise = tier === 'ENTERPRISE';
  const isTrialActive = trial?.isActive;
  const isFreeOrTrialActive = isFree || isTrialActive;
  const isManagedByAdobe = isAccountAdobeManaged(account?.managedBy);
  const delinquentGracePeriodExpiresAt =
    account?.delinquentGracePeriodExpiresAt ?? '';
  const isAccountLocked = !!account?.lockedAt;
  const isOnUsagePage = useIsOnUsagePage();

  const showBanner =
    Boolean(account?.usageByLicenseType?.guest?.overSeatLimit) &&
    !isOnUsagePage &&
    !isAccountLocked;

  if (!showBanner) return null;

  const graceDate = delinquentGracePeriodExpiresAt
    ? new Date(delinquentGracePeriodExpiresAt)
    : null;

  const titleText = canManageBilling
    ? TEXT.titleWithPermission(guestSeatsUsed, guestSeatLimit, 'Guests')
    : TEXT.titleNoPermission;

  const details = getDetails(
    canManageBilling,
    graceDate,
    isEnterprise,
    isFreeOrTrialActive,
    isManagedByAdobe,
    openUpgradeModal,
    ownerEmail,
    title
  );

  return (
    <UpgradeBannerWrapper onHoverIntent={fetchPlansData}>
      <AlertBanner details={details} title={titleText} variant="notice" />
    </UpgradeBannerWrapper>
  );
}
